/**
 * Token Refresh Lock
 *
 * Cross-tab mutex to ensure only one tab refreshes tokens at a time.
 * Uses Web Locks API as primary mechanism with localStorage fallback.
 *
 * Web Locks benefits:
 * - Truly atomic (no TOCTOU race condition)
 * - Auto-releases when tab closes (no orphaned locks)
 * - Built-in queuing (no polling needed)
 *
 * Browser support: 95.68% (Safari 15.4+, Chrome 69+, Firefox 96+)
 * Fallback: localStorage for ~4% of users on older browsers
 */

import { logger } from '../logger';
import { safeStorage } from './safeStorage';
import { amplitude, isAmplitudeInitialized } from '@lib/amplitude';
import { ANALYTICS_EVENTS } from '@lib/analytics-events';

const LOCK_NAME = 'meetball_token_refresh';
const LOCK_KEY = 'meetball_token_refresh_lock';
const LOCK_TIMEOUT_MS = 5000; // 5 seconds - max time a localStorage lock can be held

/**
 * Result of running `fn` inside a Web Lock callback.
 *
 * The discriminated shape exists so the outer `withWebLock` can tell apart
 * "lock acquired, fn ran but threw" from "lock infrastructure itself failed".
 * Only the latter triggers the localStorage fallback; the former propagates
 * the user's error directly. This prevents the doubled-call bug where falling
 * back on any error invokes `fn` a second time on the localStorage path.
 */
type WebLockFnOutcome<T> = { kind: 'fn-resolved'; value: T } | { kind: 'fn-threw'; error: unknown };

export interface TokenRefreshLockOptions {
  /**
   * Enable Web Locks API when available.
   * When false, always uses localStorage fallback regardless of browser support.
   * This allows gradual rollout of Web Locks.
   */
  enableWebLocks?: boolean;
}

export class TokenRefreshLock {
  private readonly useWebLocks: boolean;
  private isHoldingLocalStorageLock = false;

  constructor(options: TokenRefreshLockOptions = {}) {
    const { enableWebLocks = false } = options;
    const browserSupportsWebLocks = typeof navigator !== 'undefined' && 'locks' in navigator;

    // Use Web Locks only if: 1) feature flag is enabled AND 2) browser supports it
    this.useWebLocks = enableWebLocks && browserSupportsWebLocks;

    logger.debug(
      `Cross-tab sync: TokenRefreshLock initialized (Web Locks: ${this.useWebLocks}, ` +
        `flag: ${enableWebLocks}, browser: ${browserSupportsWebLocks})`,
    );

    // Track fallback usage when Web Locks was requested but browser doesn't support it
    if (enableWebLocks && !browserSupportsWebLocks) {
      this.trackFallbackUsed();
    }
  }

  /**
   * Track when localStorage fallback is used due to browser limitations.
   * Only tracked in production to avoid polluting analytics data.
   */
  private trackFallbackUsed(): void {
    if (import.meta.env.MODE === 'development' || import.meta.env.MODE === 'test') {
      return;
    }

    if (!isAmplitudeInitialized()) {
      return;
    }

    try {
      amplitude.track(ANALYTICS_EVENTS.token_refresh_fallback_used, {
        browser: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
        reason: 'web_locks_unavailable',
      });
    } catch (error) {
      // Don't let analytics errors affect auth flow
      logger.debug('Cross-tab sync: Failed to track fallback event', error);
    }
  }

  /**
   * Execute a function while holding the refresh lock.
   * This is the primary API - works for both Web Locks and localStorage.
   *
   * Web Locks: Uses callback model, auto-releases on completion or tab close.
   * localStorage: Uses acquire/release with timeout fallback.
   */
  async withLock<T>(fn: () => Promise<T>): Promise<T> {
    if (this.useWebLocks) {
      return this.withWebLock(fn);
    }
    return this.withLocalStorageLock(fn);
  }

  /**
   * Web Locks implementation - truly atomic, auto-releases on tab close.
   *
   * Falls back to localStorage ONLY when the Web Locks infrastructure itself
   * fails (e.g., `navigator.locks.request()` rejects before invoking the
   * callback — a browser quirk). When the lock was successfully acquired and
   * the wrapped `fn` ran but threw, that error is re-thrown to the caller —
   * we do NOT fall back, because falling back would invoke `fn` a second time
   * on the localStorage path. For a token refresh, that means a duplicate
   * refresh attempt with the same already-rotated refresh token.
   *
   * Discrimination is carried by `WebLockFnOutcome<T>`: the inner callback
   * always returns a discriminated outcome instead of letting `fn`'s error
   * bubble out, so the only thing that escapes `navigator.locks.request` as
   * a rejection is a lock-infrastructure failure. Don't collapse the catch
   * back into a single fallback path — the regression tests in
   * `__tests__/TokenRefreshLock.test.ts` (the `Web Locks API > withLock`
   * describe block) guard against it.
   */
  private async withWebLock<T>(fn: () => Promise<T>): Promise<T> {
    logger.debug('Cross-tab sync: Requesting Web Lock');
    let outcome: WebLockFnOutcome<T>;
    try {
      // No cast needed: lib.dom used to type navigator.locks.request as
      // Promise<any>, so the inner callback's return annotation couldn't reach
      // across the request boundary. As of TS 5.9 the overload is generic in the
      // callback's return type, so `outcome` is inferred as WebLockFnOutcome<T>
      // and an `as` here is flagged by no-unnecessary-type-assertion.
      outcome = await navigator.locks.request(
        LOCK_NAME,
        async (lock): Promise<WebLockFnOutcome<T>> => {
          if (!lock) {
            // Shouldn't happen on the path we use: we don't pass `ifAvailable`,
            // so the spec only permits null when `ifAvailable: true` is
            // requested with the lock already held. The branch is defensive in
            // case a wrapper or polyfill ever passes the flag through, and so
            // quirky implementations that hand us null don't crash. Run `fn`
            // anyway and capture its outcome the same way.
            logger.warn('Cross-tab sync: Web Lock request returned null lock');
          } else {
            logger.debug('Cross-tab sync: Web Lock acquired');
          }
          try {
            const value = await fn();
            return { kind: 'fn-resolved', value };
          } catch (error) {
            return { kind: 'fn-threw', error };
          } finally {
            logger.debug('Cross-tab sync: Web Lock releasing');
          }
        },
      );
    } catch (lockError) {
      // `fn` was never invoked (or its outcome never made it back), so the
      // failure is in `navigator.locks.request` itself — browser bug, hostile
      // environment, etc. Fall back to the localStorage path so the operation
      // can still proceed.
      logger.warn(
        'Cross-tab sync: Web Lock request failed, falling back to localStorage',
        lockError,
      );
      return this.withLocalStorageLock(fn);
    }

    if (outcome.kind === 'fn-threw') {
      // Lock was acquired and `fn` ran — error came from the wrapped
      // operation, not from the lock infrastructure. Propagate it.
      // Falling back here would call `fn` twice (the doubled-call bug).
      throw outcome.error;
    }
    return outcome.value;
  }

  /**
   * localStorage implementation - fallback for older browsers.
   * Wraps acquire/release in callback model for consistent API.
   */
  private async withLocalStorageLock<T>(fn: () => Promise<T>): Promise<T> {
    // Wait for lock to become available
    const acquired = await this.waitForLocalStorageLock();
    if (!acquired) {
      // Timed out waiting - proceed anyway (existing behavior)
      logger.warn('Cross-tab sync: Timed out waiting for localStorage lock, proceeding anyway');
      return fn();
    }

    try {
      return await fn();
    } finally {
      this.releaseLocalStorageLock();
    }
  }

  /**
   * Wait for localStorage lock to become available.
   * Returns true if acquired, false if timed out.
   */
  private async waitForLocalStorageLock(): Promise<boolean> {
    const startTime = Date.now();

    while (Date.now() - startTime < LOCK_TIMEOUT_MS) {
      if (this.tryAcquireLocalStorageLock()) {
        return true;
      }
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    return false;
  }

  /**
   * Try to acquire the localStorage lock.
   * Returns true if successful, false if another tab holds the lock.
   */
  private tryAcquireLocalStorageLock(): boolean {
    const now = Date.now();
    const existingLock = safeStorage.getItem(LOCK_KEY);

    if (existingLock) {
      const lockTime = parseInt(existingLock, 10);
      if (now - lockTime < LOCK_TIMEOUT_MS) {
        // Lock is held by another tab and not expired
        return false;
      }
      // Lock expired, we can take it
      logger.debug('Cross-tab sync: Taking over expired localStorage lock');
    }

    // Acquire the lock
    const lockAcquired = safeStorage.setItem(LOCK_KEY, now.toString());
    if (!lockAcquired) {
      logger.warn(
        'Cross-tab sync: Could not write lock to storage, proceeding without cross-tab coordination',
      );
    }
    this.isHoldingLocalStorageLock = true;
    logger.debug('Cross-tab sync: localStorage lock acquired');
    return true;
  }

  /**
   * Release the localStorage lock if we're holding it.
   */
  private releaseLocalStorageLock(): void {
    if (this.isHoldingLocalStorageLock) {
      safeStorage.removeItem(LOCK_KEY);
      this.isHoldingLocalStorageLock = false;
      logger.debug('Cross-tab sync: localStorage lock released');
    }
  }

  /**
   * Check if another tab is currently holding the lock.
   * For Web Locks path, this always returns false - use CrossTabAuthSync.isOtherTabRefreshing()
   */
  isOtherTabHoldingLock(): boolean {
    if (this.useWebLocks) {
      // Web Locks: We track via BroadcastChannel in CrossTabAuthSync
      return false;
    }

    if (this.isHoldingLocalStorageLock) return false; // We're the one holding it

    const lockValue = safeStorage.getItem(LOCK_KEY);
    if (!lockValue) return false;

    const lockTime = parseInt(lockValue, 10);
    return Date.now() - lockTime < LOCK_TIMEOUT_MS;
  }

  /**
   * Clean up stale locks (from crashed tabs).
   * Call this on initialization. Only relevant for localStorage path.
   */
  cleanupStaleLock(): void {
    if (this.useWebLocks) {
      // Web Locks auto-cleanup on tab close - nothing to do
      return;
    }

    const lockValue = safeStorage.getItem(LOCK_KEY);
    if (lockValue) {
      const lockTime = parseInt(lockValue, 10);
      if (Date.now() - lockTime > LOCK_TIMEOUT_MS) {
        logger.debug('Cross-tab sync: Cleaning up stale localStorage lock');
        safeStorage.removeItem(LOCK_KEY);
      }
    }
  }

  /**
   * Check if Web Locks API is being used.
   */
  isUsingWebLocks(): boolean {
    return this.useWebLocks;
  }
}
